import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import api from "../../utils/api";
import { IMG_BASE_URL } from "../../constants";
import {
  AnalyticsMode,
  MyOrganization,
  OrganizationAPIResponse,
} from "./organizationInterfaces";
import { DecisionStatus } from "../applications/applicationInterfaces";
export interface OrganizationState {
  loading: boolean;
  id?: number;
  slug?: string;
  imgUrl?: string;
  name?: string;
  meta?: {
    /**
     * Whether embedded analytics is available for this organization. The
     * dashboard URL is no longer sent to the browser: it is minted per request,
     * signed and short-lived, by GET /organizations/:slug/analytics/embed.
     */
    analyticsEnabled?: boolean;
    analyticsMode?: AnalyticsMode;
    /** Deprecated by the API; read only when `analyticsMode` is absent. */
    sigmaUrl?: string;
    clientFooterLogo?: string;
    clientPrivacyPolicy?: string;
    acquireFraudGuardDemoMode?: boolean;
    gtmId?: string;
    enableDecisioning?: boolean;
    displayOnboardingApplicationResponses?: boolean;
    enableDecisionIntelligence?: boolean | "v1" | "v2";
    academyBaseUrl?: string;
    hideDecisionIntelligenceDetails?: boolean;
    agenticAIBaseUrl?: string;
    settingsBaseUrl?: string;
    promoUrl?: string;
    promoLabel?: string;
    applicationsOverviewUseMockData?: boolean; // Use mock data for applications overview (demo mode)
    customMenu?: {
      icon?: string;
      title: string;
      links?: {
        icon?: string;
        title: string;
        action: string;
        target?: string;
      }[];
    };
  };
  decisionStatuses?: string[];
}

const initialState: OrganizationState = {
  loading: false,
};

export const getOrganizationBySlug = createAsyncThunk<MyOrganization, string>(
  "organization/getOrganizationBySlug",
  async (orgSlug: string) => {
    // Get initial organization data
    const {
      data: { logoId, logoVersion, id, slug, name, meta },
    } = await api.get<OrganizationAPIResponse>(`/organizations/${orgSlug}`);

    const result: MyOrganization = {
      slug,
      id,
      imgUrl: `${IMG_BASE_URL}/${logoVersion}/${logoId}`,
      name,
      meta,
    };
    return result;
  }
);

export const getDecisionStatuses = createAsyncThunk<string[], string>(
  "applications/getApplicationDecisionStatuses",
  async (orgSlug: string) => {
    const result = await api.get<Array<DecisionStatus>>(
      `/organizations/${orgSlug}/decision-statuses`
    );
    const statuses = result.data || [];

    return Array.from(new Set(statuses.map((s) => s.title))).sort();
  }
);

export const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {},
  extraReducers: {
    [getOrganizationBySlug.pending.type]: (state) => {
      state.loading = true;
    },
    [getOrganizationBySlug.fulfilled.type]: (
      state,
      { payload }: PayloadAction<MyOrganization>
    ) => {
      return { ...payload, loading: false };
    },
    [getDecisionStatuses.fulfilled.type]: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.decisionStatuses = action.payload;
    },
  },
});

export const selectOrg = (state: RootState) => state.organization;

export default organizationSlice.reducer;
